import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import SubjectIcon from '@mui/icons-material/Subject';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import moment from 'moment';
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, RefreshDataEnum } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 480,
        maxWidth: 520,
        height: 'fit-content',
        padding: '20px 24px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: 'none',
        boxShadow: '0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 20px 25px -5px rgba(0, 0, 0, 0.1)',
    },
    container: {
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 8,
        borderRadius: 8,
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: '8px 12px',
            "& > textarea": {
                fontFamily: 'Lato',
                fontSize: '0.875rem',
                color: '#475569',
                padding: 'unset',
            },
        },
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff !important',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
    icon: {
        width: 24,
        height: 24,
        padding: 4,
        "& > .MuiSvgIcon-root": {
            fill: '#94A3B8',
            width: 16,
            height: 16,
        }
    },
    titleDashboard: {
        fontFamily: 'Inter',
        fontSize: '0.9375rem',
        fontWeight: 400,
        color: '#64748B',
    },
    titleField: {
        fontFamily: 'Inter',
        fontSize: '0.9375rem',
        fontWeight: 700,
        color: '#1E293B',
    },
    latestCard: {
        padding: '16px 20px 20px',
        borderRadius: 12,
        backgroundColor: '#F0FDFA',
        border: '1px solid #CCFBF1',
    },
    latestBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'Inter',
        fontSize: '11px',
        fontWeight: 600,
        color: '#0D9488',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: 12,
    },
    latestText: {
        fontFamily: 'Lato',
        fontSize: '0.875rem',
        color: '#334155',
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap' as const,
    },
    historyTitle: {
        fontFamily: 'Inter',
        fontSize: '11px',
        fontWeight: 600,
        color: '#9CA3AF',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
    },
    historyItem: {
        position: 'relative' as const,
        paddingLeft: 20,
        paddingBottom: 20,
        '&:last-child': {
            paddingBottom: 0,
        },
        '&:first-child': {
            paddingTop: 0,
        },
        // Vertical timeline line
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 3,
            top: 12,
            bottom: 0,
            width: 2,
            backgroundColor: '#D1D5DB',
        },
        '&:last-child::before': {
            display: 'none',
        },
    },
    historyTimeline: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
    },
    historyTimeAgo: {
        fontFamily: 'Inter',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: '#1F2937',
    },
    historyDash: {
        fontFamily: 'Inter',
        fontSize: '0.8125rem',
        fontWeight: 400,
        color: '#9CA3AF',
        padding: '0 2px',
    },
    historyDate: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 400,
        color: '#9CA3AF',
    },
    historyText: {
        fontFamily: 'Lato',
        fontSize: '0.875rem',
        color: '#64748B',
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap' as const,
    },
    historySection: {
        maxHeight: 350,
        overflowY: 'auto' as const,
        ...scrollbarStyle,
    },
}));

const CustomFieldContentCard: React.FC<{
    dashboard: Dashboard,
    fieldName: string,
    newValue?: boolean,
    onClose: () => void,
}> = ({ dashboard, fieldName, newValue, onClose }) => {
    const classes = useStyles();
    const { setDashboards } = useContext(DashboardsContext);
    const { isPublicView } = useContext(DashboardContext);

    const [editable, setEditable] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [editValue, setEditValue] = useState<string>('');

    const customFieldValues = useMemo(() =>
        dashboard?.customFields?.find(({ name }) => name === fieldName)?.value as string
        , [dashboard?.customFields, fieldName]);

    const fieldHistory = useMemo(() => {
        if (!dashboard.history || dashboard.history.length === 0) return [];
        return dashboard.history
            .filter(e => e.customFields?.some(cf => cf.name === fieldName))
            .map(e => ({
                value: e.customFields!.find(cf => cf.name === fieldName)!.value as string,
                timestamp: e.changedAt,
            }))
            .sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp)));
    }, [dashboard.history, fieldName]);

    const handleSave = useCallback(async () => {
        setLoading(true);

        const fieldExists = (dashboard.customFields ?? []).some(cf => cf.name === fieldName);
        const updatedCustomFields = (() => {
            const mapped = (dashboard.customFields ?? []).map(cf => ({
                name: cf.name,
                value: cf.name === fieldName ? editValue : (Array.isArray(cf.value) ? cf.value.join(', ') : (cf.value ?? '')),
                manualOverride: cf.name === fieldName ? true : cf.manualOverride,
            }));
            if (!fieldExists) {
                mapped.push({ name: fieldName, value: editValue, manualOverride: true });
            }
            return mapped;
        })();

        const dashboardUpdate: Dashboard = {
            ...dashboard!,
            customFields: updatedCustomFields,
            tagGroups: (dashboard.tagGroups ?? []).map((g) => ({ ...g })),
            refreshData: {
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Notes,
            }
        };

        const result = await updateDashboardFunc(dashboardUpdate);
        if (result) {
            // Use backend response (includes computed history), fall back to local update
            const updated = typeof result === 'object' ? { ...dashboardUpdate, ...result } : dashboardUpdate;
            setDashboards(prev => prev.map(d => d.id === dashboard.id ? updated : d));
        }
        setLoading(false);
        setSaveReady(false);
        setEditable(false);
        // eslint-disable-next-line
    }, [dashboard, fieldName, editValue]);

    useEffect(() => {
        if (newValue) {
            setEditable(true);
            setEditValue(customFieldValues ?? '');
            setSaveReady(true);
        }
        // eslint-disable-next-line
    }, []);

    return (<>
        <Stack direction="column" className={classes.card} spacing={2.5} justifyContent="stretch">
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%">
                <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ overflow: 'hidden', minWidth: 0 }}>
                    <Typography className={classes.titleDashboard} noWrap>
                        {dashboard.title}
                    </Typography>
                    <Typography className={classes.titleDashboard} sx={{ flexShrink: 0 }}>{' - '}</Typography>
                    <Typography className={classes.titleField} noWrap>
                        {fieldName}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {(!editable && !isPublicView) && (
                        <IconButton className={classes.icon} disableRipple size="small"
                            onClick={() => { setEditValue(customFieldValues ?? ''); setEditable(true); }}>
                            <SubjectIcon />
                        </IconButton>
                    )}
                    <IconButton className={classes.icon} disableRipple size="small"
                        onClick={() => onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Stack>

            {/* Latest value card */}
            <Box className={classes.latestCard}>
                <Box className={classes.latestBadge}>
                    <TrendingUpIcon sx={{ width: 14, height: 14 }} />
                    {'Latest · ' + (fieldHistory.length > 0
                        ? moment(fieldHistory[0].timestamp).format('MMM YYYY').toUpperCase()
                        : moment(dashboard.updatedAt ?? dashboard.createdAt).format('MMM YYYY').toUpperCase()
                    )}
                </Box>
                {editable ? (
                    <Stack spacing={1.5}>
                        <TextField variant="outlined"
                            className={classNames('no-border', classes.inputField)}
                            inputProps={{ style: { width: '100%' }, className: 'no-fade' }}
                            value={editValue}
                            placeholder={!isPublicView ? 'Add your comments here...' : ''}
                            onChange={(e) => { setEditValue(e.target.value); setSaveReady(!!e.target.value); }}
                            disabled={isPublicView || !editable}
                            minRows={3}
                            maxRows={5}
                            multiline
                            sx={{ backgroundColor: '#fff', borderRadius: '8px' }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%">
                            <Button variant="outlined" size="small"
                                className={classes.cancelButton}
                                onClick={() => setEditable(false)}
                            > {'Cancel'} </Button>
                            <LoadingButton variant="contained" size="small"
                                className={classes.saveButton}
                                loading={loading}
                                disabled={!saveReady}
                                onClick={() => handleSave()}
                            > {'Save'} </LoadingButton>
                        </Stack>
                    </Stack>
                ) : (
                    <Typography className={classes.latestText}>
                        {customFieldValues || 'No value set'}
                    </Typography>
                )}
            </Box>

            {/* History section */}
            {fieldHistory.length > 1 && (
                <Box>
                    <Typography className={classes.historyTitle} sx={{ marginBottom: '12px' }}>
                        {'History'}
                    </Typography>
                    <Box className={classes.historySection}>
                        {fieldHistory.slice(1).map((item, i) => (
                            <Box key={'cf-history-' + i} className={classes.historyItem}>
                                <CircleIcon sx={{ position: 'absolute', left: 0, top: 6, width: 8, height: 8, color: i === 0 ? '#5EBBAE' : '#C4E8E2' }} />
                                <Box className={classes.historyTimeline}>
                                    <Typography className={classes.historyTimeAgo}>
                                        {moment(item.timestamp).fromNow()}
                                    </Typography>
                                    <Typography className={classes.historyDash}>{'—'}</Typography>
                                    <Typography className={classes.historyDate}>
                                        {moment(item.timestamp).format('D MMM YYYY')}
                                    </Typography>
                                </Box>
                                <Typography className={classes.historyText}>
                                    {item.value || 'N/A'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    </>);
};

export default memo(CustomFieldContentCard);
